import { substituteVariables } from "$lib/utils/variableSubstitution"
import { getCurrencySymbol } from "$lib/utils/currencySymbol"

function replaceCompetitionUrl(competition, terms) {
  if (competition.cname) {
    return terms.replaceAll(
      "https://www.promoamp.com/c/[competition-url]",
      `https://${competition.cname}`,
    )
  }
  return terms.replaceAll("[competition-url]", competition.slug)
}

export function generateTerms(competition) {
  const termsAndRules = competition?.termsAndRules

  // If using custom terms with variables, substitute them
  if (!termsAndRules?.useAutomaticTerms && termsAndRules?.termsAndConditions) {
    return substituteVariables(termsAndRules.termsAndConditions, competition)
  }

  const giveawaySlug = competition.slug ? competition.slug : "[competition-url]"
  let giveawayUrl = `https://www.promoamp.com/c/${giveawaySlug}`
  if (
    competition.cname &&
    competition?.cnameDetails?.status?.toLowerCase() === "active"
  ) {
    giveawayUrl = `https://${competition.cname}`
  }

  return [
    `<p><b>No Purchase Necessary:</b> No purchase is required to enter or win the competition. Making a purchase will not improve your chances of winning.</p>`,

    `<p><b>Sponsor:</b> The ${competition.title} (the "Competition") is sponsored by ${termsAndRules?.sponsorName || "the Sponsor"}.</p>`,

    `<p><b>Eligibility:</b> The Competition is open to individuals who register at ${giveawayUrl}. ${
      competition.countries?.length === 1 && competition.countries[0] === "XX"
        ? "It is open to participants worldwide."
        : competition.countries?.length
          ? `It is open to participants from ${competition.countries.join(", ")}.`
          : "It is open to participants worldwide, except where restricted by law."
    } Employees, contractors, interns, officers, directors, and agents of ${
      termsAndRules?.sponsorName || "the Sponsor"
    }, its affiliates, subsidiaries, advertising agencies, suppliers, and immediate family members or individuals residing in the same household as any of the excluded parties, are not eligible to enter or win. The Competition is governed by all relevant laws and regulations.</p>`,

    `<p><b>Agreement to Rules:</b> By participating in the Competition, you (the "Entrant") agree to comply with these terms and conditions and the decisions of the Sponsor, which are final and binding. Any questions or concerns must be directed to the Sponsor.</p>`,

    `<p><b>Entry Period:</b> The Competition begins at ${new Date(competition.startDate).toLocaleString()} and concludes at ${new Date(competition.endDate).toLocaleString()} ${competition.timezone?.label || ""}. All times are in the ${competition.timezone?.value || "local"} timezone. Any entries submitted outside of this period will not be accepted.</p>`,

    `<p><b>How to Enter:</b> To enter, submit your details through the provided online form at ${giveawayUrl}. All mandatory fields must be completed for your entry to be valid. Incomplete or non-compliant entries may be disqualified. Multiple entries via different email addresses or other fraudulent methods are prohibited and may lead to disqualification.</p>`,

    `<p><b>Prizes:</b> ${
      competition.prizes?.length
        ? competition.prizes
            .map((prize, index) => {
              const currency = prize.currency || "USD"
              const symbol = getCurrencySymbol(currency)
              return `${index === 0 ? "Prizes include: " : ""}${prize.name} (${symbol}${prize.value})`
            })
            .join(". ")
        : "Prizes will be determined by the Sponsor."
    }. The exact value of the prizes may vary at the time of award, and all prize details will be determined by the Sponsor at its discretion. Prizes are not transferable or redeemable for cash. The winner is responsible for any taxes or expenses associated with claiming the prize. By accepting the prize, the winner consents to the Sponsor using their name and likeness for promotional purposes, unless prohibited by law.</p>`,

    `<p><b>Odds of Winning:</b> The odds of winning will depend on the total number of eligible entries received.</p>`,

    `<p><b>Winner Selection and Notification:</b> The winner will be selected by a random draw. They will be notified within ${numberToWords(termsAndRules.contactWinnerWithinDays)} (${termsAndRules.contactWinnerWithinDays}) days of the draw. The Sponsor is not liable for any issues that prevent notification, such as incorrect contact details or spam filters. If the winner does not respond or claim the prize within ${numberToWords(termsAndRules.claimPrizeWithinDays)} (${termsAndRules.claimPrizeWithinDays}) days of notification, the Sponsor reserves the right to select an alternate winner.</p>`,

    `<p><b>Rights Granted by Entrants:</b> By entering the Competition, you grant the Sponsor the right to use your name, image, and entry for promotional purposes in perpetuity, without any further compensation. You also confirm that your entry is your own original work and does not violate any third-party rights.</p>`,

    `<p><b>Terms and Conditions:</b> The Sponsor reserves the right to modify, suspend, or cancel the Competition if it is compromised by technical failures, unauthorised intervention, or other circumstances that affect its fairness or integrity. Any person attempting to tamper with the entry process or breach these terms and conditions may be disqualified. Automated methods of entry, including the use of bots, are prohibited.</p>`,

    `<p><b>Limitation of Liability:</b> By participating in the Competition, you release ${competition.profile?.company_name || "the Sponsor"}, its affiliates, subsidiaries, and related parties from any liability arising from your participation in the Competition or acceptance of any prize.</p>`,

    `<p><b>Disputes:</b> This Competition is governed by the laws of ${termsAndRules.governingLawCountry}. Any disputes or claims arising in connection with the Competition will be resolved individually in Australia, and Entrants waive the right to seek damages beyond their actual out-of-pocket expenses.</p>`,

    `<p><b>Winners List:</b> To obtain a list of winners, contact the Sponsor within four (4) weeks following the end of the Competition.</p>`,

    `<p><b>Platform Disclaimer:</b> This Competition is not sponsored, endorsed, or administered by, or associated with any social media platforms, including Facebook, Instagram, or Twitter. You are providing your information to the Sponsor and not to these platforms. Promo Amp, which powers the competition platform, is not responsible for the administration of this Competition and is not involved in its management. By entering, you agree to Promo Amp's Privacy Policy.</p>`,

    `<p><b>Acceptance of Rules:</b> By entering the Competition, you confirm your acceptance of these Terms and Conditions.</p>`,

    `${
      termsAndRules?.additionalTerms
        ? `<p><b>Additional Terms:</b> ${termsAndRules.additionalTerms}</p>`
        : ""
    }`,
  ].join("\n")
}

function numberToWords(num: number) {
  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
  ]
  const tens = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ]
  const teens = [
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ]

  if (num < 10) return ones[num]
  if (num < 20) return teens[num - 10]
  if (num < 100) {
    return tens[Math.floor(num / 10)] + (num % 10 ? "-" + ones[num % 10] : "")
  }
  return String(num) // fallback for numbers >= 100
}
